/**
 * ResumeChecklistCard — Installer Portal Jobs tab shortcut.
 *
 * One active checklist  → single prominent Resume card.
 * Multiple active items → compact "Active Checklists" list.
 * Zero                  → renders nothing.
 *
 * Read-only navigation only. No writers.
 */
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ClipboardCheck, ChevronRight, MapPin } from "lucide-react";
import type { ResumableChecklist } from "@/data/contractor/resumeChecklist";

export function ResumeChecklistCard({ items }: { items: ResumableChecklist[] }) {
  if (items.length === 0) return null;

  if (items.length === 1) {
    const item = items[0];
    return (
      <Link
        to={item.href}
        data-testid="resume-checklist-card"
        data-job-id={item.jobId}
        className="block"
      >
        <Card className="border-primary/40 bg-primary/5 hover:bg-primary/10 transition-colors">
          <CardContent className="p-4 flex items-center gap-3 min-h-[72px]">
            <div className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
              <ClipboardCheck className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold text-primary uppercase tracking-wider">
                Resume Checklist
              </p>
              <p className="text-sm font-semibold truncate">{item.customerName}</p>
              <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
                <MapPin className="h-3 w-3 shrink-0" />
                <span className="truncate">{item.addressCity}</span>
                <span className="mx-1">·</span>
                <span className="truncate">{item.currentPhase}</span>
              </p>
            </div>
            <ChevronRight className="h-5 w-5 text-primary shrink-0" />
          </CardContent>
        </Card>
      </Link>
    );
  }

  return (
    <Card
      data-testid="active-checklists-list"
      className="border-primary/40 bg-primary/5"
    >
      <CardContent className="p-3 space-y-2">
        <p className="text-[10px] font-bold text-primary uppercase tracking-wider px-1">
          Active Checklists
        </p>
        <ul className="space-y-1.5">
          {items.map((item) => (
            <li
              key={item.jobId}
              data-testid="active-checklist-item"
              data-job-id={item.jobId}
              className="flex items-center gap-2 rounded-md border border-border/60 bg-card px-2.5 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold truncate">{item.customerName}</p>
                <p className="text-[10px] text-muted-foreground truncate">
                  {item.addressCity} · {item.currentPhase}
                </p>
              </div>
              <Link to={item.href} className="shrink-0">
                <Button
                  size="sm"
                  variant="default"
                  className="h-8 px-3 text-xs min-w-[72px]"
                >
                  Resume
                </Button>
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export default ResumeChecklistCard;
